import api from './api';

/**
 * ============================================================
 * WEB PUSH - browser se permission aur subscription
 * ============================================================
 *
 * ⚠️ FLOW:
 *   1. Browser se notification permission maango
 *   2. Service worker se subscription lo (endpoint + 2 keys)
 *   3. Backend ko bhejo
 *   4. Backend us endpoint pe notification bhejta hai
 *
 * ⚠️ SUBSCRIPTION MEIN "KISKA HAI" NAHI BHEJTE.
 *
 * Backend token se khud nikalta hai. Yahan se userId bhejte to
 * koi bhi doosre ki ID daal ke uski notifications apne phone pe
 * le leta - "aapki booking accept ho gayi" jaisi cheezein
 * doosre ko dikhti.
 */

/**
 * Base64 (URL-safe) ko Uint8Array mein badlo.
 *
 * ⚠️ Zaroori hai: browser ka pushManager.subscribe()
 * applicationServerKey ko Uint8Array mein maangta hai, string
 * mein nahi. Seedha string dete hain to "InvalidCharacterError"
 * aati hai aur wajah samajh nahi aati.
 *
 * VAPID key URL-safe base64 hai (- aur _ ke saath), isliye
 * pehle usse normal base64 banate hain (+ aur /), phir decode.
 */
const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};

/**
 * Browser push support karta hai?
 *
 * ⚠️ iPhone pe ye tabhi true hota hai jab site home screen pe
 * add ki gayi ho. Safari ke normal tab mein false aayega - wo
 * bug nahi hai, Apple ka niyam hai.
 */
export const isPushSupported = () => {
    return 'serviceWorker' in navigator
        && 'PushManager' in window
        && 'Notification' in window;
};

/** Abhi permission ki kya haalat hai */
export const getPermissionStatus = () => {
    if (!isPushSupported()) return 'unsupported';
    return Notification.permission; // 'default' | 'granted' | 'denied'
};

/**
 * Notification on karo.
 *
 * @returns {Promise<boolean>} safal hua ya nahi
 */
export const enablePush = async () => {
    if (!isPushSupported()) {
        return false;
    }

    // ⚠️ Pehle se block kiya hua ho to dobara maangne ka fayda
    // nahi - browser popup dikhata hi nahi. User ko browser
    // settings se khud on karna padta hai.
    if (Notification.permission === 'denied') {
        return false;
    }

    try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            return false;
        }

        // Service worker ready hone ka intezaar
        const registration = await navigator.serviceWorker.ready;

        // ⚠️ Public key BACKEND se laate hain, frontend mein
        // hardcode nahi karte. Keys deploy ke waqt badalni hain,
        // aur do jagah rakhte to ek jagah badalna bhool jaate -
        // phir push chupchaap band ho jaata.
        const { data } = await api.get('/api/push/public-key');

        let subscription = await registration.pushManager.getSubscription();

        // ⚠️ Purana subscription ho to hata ke naya banao.
        //
        // Kyun: VAPID key badalne pe purana subscription bekaar
        // ho jaata hai, par browser use rakhe rehta hai. Naya
        // banaye bina push kabhi nahi aayega aur koi error bhi
        // nahi dikhega.
        if (subscription) {
            await subscription.unsubscribe();
        }

        subscription = await registration.pushManager.subscribe({
            // ⚠️ true zaroori hai - browser sirf tabhi allow
            // karta hai jab har push pe notification DIKHAYI
            // jaaye. Silent push allowed nahi hai.
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(data.publicKey)
        });

        const json = subscription.toJSON();

        await api.post('/api/push/subscribe', {
            endpoint: subscription.endpoint,
            p256dh: json.keys.p256dh,
            auth: json.keys.auth
        });

        return true;

    } catch (err) {
        console.error('Push enable failed:', err);
        return false;
    }
};

/**
 * Notification band karo.
 */
export const disablePush = async () => {
    if (!isPushSupported()) return;

    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
            // ⚠️ Backend ko PEHLE batao, phir browser se hatao.
            //
            // Ulta karte to endpoint pata hi na chalta aur DB
            // mein dead row padi rehti - us par har notification
            // pe koshish hoti rehti aur time barbaad hota.
            await api.post('/api/push/unsubscribe', {
                endpoint: subscription.endpoint
            });
            await subscription.unsubscribe();
        }
    } catch (err) {
        console.error('Push disable failed:', err);
    }
};